import React, { useState } from 'react';
import { ItemTransfer, useGetItemTransfersQuery, useLazyGetProcurementProcessesQuery, useLazyGetPurchaseOrdersQuery, } from '../../../redux/vmsApi';
import {
  Box,
  Button,
  Chip,
  Dialog,
  FormControl,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import config from "../../../config";
import { formatCurrency } from '../../../utils/currencyUtil';
import { getCurrentDate } from "../../../utils/dateUtil";
import { useRouter } from '../../../hooks/useRouter';

const GRVList: React.FC = () => {
  const router = useRouter();
  const [triggerFetch] = useLazyGetProcurementProcessesQuery();
  const [triggerFetchPurchaseOrder] = useLazyGetPurchaseOrdersQuery();

  const handleViewProcessDetails = (procurementProcessId: string | undefined) => {
    router.push(`/procurement/procurementProcess/${procurementProcessId}`);
  };

  const handleDialogOpen = async (
    selectedGrv: ItemTransfer,
    documentType: 'purchaseOrder' | 'requisition' | 'quotation' | 'grv'
  ) => {
    const procurementProcessId = selectedGrv.procurementProcessId;
    try {
      const result = await triggerFetch({ procurementProcessId });
      const purchaseOrder = await triggerFetchPurchaseOrder({ procurementProcessId });
      if (!purchaseOrder?.data || purchaseOrder?.data?.length === 0) {
        console.error("purchase order  not found");
        return;
      }
      const processes = result.data;
      if (!processes || processes.length === 0) {
        console.error("Procurement process not found");
      } else {
        const process = processes[0];

        const fileName =
          documentType === "purchaseOrder"
            ? process.purchaseOrderDocument?.name
            : documentType === "quotation"
              ? purchaseOrder?.data[0].quotation?.attachments?.[0]?.name
              : documentType === "grv"
                ? selectedGrv?.grvDocument?.name
                : process.requisitionDocument?.name;


        if (!fileName) {
          console.error("No document attached");
        }
        setOpenDialog(`${config.CDN_URL}/procurement/${fileName}`);
      }

    } catch (e) {
      console.error("Error opening document dialog", e);
      setOpenDialog(null);
    }
  };

  const handleDialogClose = () => setOpenDialog(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGrv, setselectedGrv] = useState<ItemTransfer| null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>, grv: ItemTransfer) => {
    setAnchorEl(event.currentTarget);
    setselectedGrv(grv);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [filters, setFilters] = useState({
    title: '',
    transferredBy: '',
    transferType: 'Goods Received',
    grvNumber: '',
    quotationNumber: '',
    purchaseOrderNumber: '',
    supplier: '',
    currency: '',
    fromDate: '2025-01-01',
    toDate: getCurrentDate(),
    isService: false,
    sourceLocationType: 'supplier',
    destinationLocationType: ''
  });

  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });

  const { data: itemTransfers, isLoading, isError } = useGetItemTransfersQuery(apiParams);

  const handleFilterChange = (field: string, value: string | boolean) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setApiParams({
      ...filters,
      skip: 0,
      limit: pageSize,
    });
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setApiParams((prevParams) => ({
      ...prevParams,
      skip: newPage * pageSize,
    }));
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    setApiParams((prevParams) => ({
      ...prevParams,
      limit: newSize,
      skip: 0,
    }));
  };

  const getLocationName = (location: any) => {
    if (location?.unit?.name) return `Unit: ${location.unit.name}`;
    if (location?.project?.name) return `Project: ${location.project.name}`;
    if (location?.development?.name) return `Development: ${location.development.name}`;
    if (location?.inventoryLocation?.locationCode) return `Inventory: ${location.inventoryLocation.locationCode}`;
    if (location?.supplier?.name) return `Supplier: ${location.supplier.name}`;
    return 'N/A';
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error fetching item transfers.</div>;
  }

  return (
    <React.Fragment>
      <Box sx={{ padding: 3, borderRadius: 2, marginBottom: 3, backgroundColor: '#f9f9f9', boxShadow: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
          Filter
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">

          <TextField
            label="Title"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.title}
            onChange={(e) => handleFilterChange('title', e.target.value)}
          />
          <TextField
            label="Transferred By"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.transferredBy}
            onChange={(e) => handleFilterChange('transferredBy', e.target.value)}
          />
          <TextField
            label="PO Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.purchaseOrderNumber}
            onChange={(e) => handleFilterChange('purchaseOrderNumber', e.target.value)}
          />
          <TextField
            label="Quotation Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.quotationNumber}
            onChange={(e) => handleFilterChange('quotationNumber', e.target.value)}
          />
          <TextField
            label="Currency"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.currency}
            onChange={(e) => handleFilterChange('currency', e.target.value)}
          />
          <TextField
            label="GRV Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.grvNumber}
            onChange={(e) => handleFilterChange('grvNumber', e.target.value)}
          />

          <TextField
            label="Supplier Name"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.supplier}
            onChange={(e) => handleFilterChange('supplier', e.target.value)}
          />


          <FormControl fullWidth sx={{ maxWidth: 240 }}>
            <Select
              value={filters.sourceLocationType || ''}
              onChange={(e) => handleFilterChange('sourceLocationType', e.target.value)}
              displayEmpty
              size="small"
              sx={{ backgroundColor: '#fff', borderRadius: 1 }}
            >
              <MenuItem value="">All Source Types</MenuItem>
              <MenuItem value="unit">Unit</MenuItem>
              <MenuItem value="project">Project</MenuItem>
              <MenuItem value="development">Development</MenuItem>
              <MenuItem value="inventory">Inventory</MenuItem>
              <MenuItem value="supplier">Supplier</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ maxWidth: 240 }}>
            <Select
              value={filters.destinationLocationType || ''}
              onChange={(e) => handleFilterChange('destinationLocationType', e.target.value)}
              displayEmpty
              size="small"
              sx={{ backgroundColor: '#fff', borderRadius: 1 }}
            >
              <MenuItem value="">All Destination Types</MenuItem>
              <MenuItem value="unit">Unit</MenuItem>
              <MenuItem value="project">Project</MenuItem>
              <MenuItem value="development">Development</MenuItem>
              <MenuItem value="inventory">Inventory</MenuItem>
              <MenuItem value="supplier">Supplier</MenuItem>
            </Select>
          </FormControl>


          <TextField
            label="From Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
          />
          <TextField
            label="To Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
          />


          <FormControl fullWidth sx={{ maxWidth: 240 }}>
            <Select
              value={filters.isService ? 'true' : 'false'}
              onChange={(e) => handleFilterChange('isService', e.target.value === 'true')}
              size="small"
              sx={{ backgroundColor: '#fff', borderRadius: 1 }}
            >
              <MenuItem value="true">Service</MenuItem>
              <MenuItem value="false">Non-Service</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Stack direction="row" spacing={2} mt={2}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={handleApplyFilters}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#1565c0',
              },
            }}
          >
            Apply Filters
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => setFilters({
              title: '',
              transferredBy: '',
              transferType: 'Goods Received',
              grvNumber: '',
              quotationNumber: '',
              currency: '',
              purchaseOrderNumber: '',
              sourceLocationType: '',
              supplier: '',
              destinationLocationType: '',
              fromDate: '2025-01-01',
              toDate: getCurrentDate(),
              isService: false
            })}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#f44336',
              borderColor: '#f44336',
              '&:hover': {
                backgroundColor: '#ffebee',
                borderColor: '#e53935',
              },
            }}
          >
            Clear Filters
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Received By</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PO Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supplier</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Destination</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Grv number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>GRV Value</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {itemTransfers && itemTransfers.length > 0 ? (
              itemTransfers.map((transfer: any, index: number) => (
                <TableRow key={transfer.id} sx={{ backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                  <TableCell>{transfer.title || 'N/A'}</TableCell>
                  <TableCell>{transfer.transferredBy.name || 'N/A'}</TableCell>
                  <TableCell>{transfer.purchaseOrderNumber || 'N/A'}</TableCell>
                  <TableCell>{getLocationName(transfer.sourceLocation)}</TableCell>
                  <TableCell>{getLocationName(transfer.destinationLocation)}</TableCell>
                  <TableCell>
                    {new Date(transfer.transferredDate).toLocaleDateString('en-GB', {
                      month: 'short', day: 'numeric', year: 'numeric'
                    })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transfer.isService ? 'Service' : 'Goods'}
                      color={transfer.isService ? 'primary' : 'secondary'}
                    />
                  </TableCell>

                  <TableCell>
                    {transfer.grvNumber || 'N/A'}
                  </TableCell>
                

                  <TableCell>
                    {formatCurrency(transfer.grandTotal, transfer.currency || 'usd')}
                  </TableCell>
                    <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => handleOpen(e, transfer)}
                    >
                      Actions
                    </Button>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      slotProps={{
                        paper: {
                          elevation: 3,
                          sx: {
                            minWidth: 200,
                            borderRadius: 1,
                          },
                        }
                      }}
                    >
                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedGrv?.procurementProcessId) {
                          handleDialogOpen(selectedGrv, 'grv');
                        }
                      }}>
                        View GRV Document
                      </MenuItem>

                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedGrv?.procurementProcessId) {
                          handleDialogOpen(selectedGrv, 'purchaseOrder');
                        }
                      }}>
                        View Purchase Order Document
                      </MenuItem>

                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedGrv) {
                          handleDialogOpen(selectedGrv, 'quotation');
                        }
                      }}>
                        View Quotation Document
                      </MenuItem>

                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedGrv) {
                          handleDialogOpen(selectedGrv, 'requisition');
                        }
                      }}>
                        View Requisition Document
                      </MenuItem>

                      {selectedGrv?.procurementProcessId && (
                        <MenuItem onClick={() => {
                          handleClose();
                          handleViewProcessDetails(selectedGrv.procurementProcessId);
                        }}>
                          View Process Details
                        </MenuItem>
                      )}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">No matching transfers found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>


      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={() => handlePageChange(Math.max(page - 1, 0))}
          disabled={page === 0}
          variant="contained"
          sx={{ marginRight: 2 }}
        >
          Previous
        </Button>
        <Typography variant="body1">Page {page + 1}</Typography>
        <Button
          onClick={() => handlePageChange(page + 1)}
          disabled={itemTransfers?.length === undefined || itemTransfers.length < pageSize}
          variant="contained"
          sx={{ marginLeft: 2 }}
        >
          Next
        </Button>
      </Box>

      <Box mt={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2" sx={{ marginRight: 2 }}>Rows per page:</Typography>
        <FormControl variant="outlined" size="small">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{ backgroundColor: '#f0f4f7' }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>


      <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
        <Box p={2}>
          {openDialog && (
            <iframe
              src={openDialog}
              title="Document Viewer"
              width="100%"
              height="500px"
              style={{ border: 'none' }}
            />
          )}
          <Box textAlign="right" mt={2}>
            <Button onClick={handleDialogClose} variant="contained" color="primary">
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </React.Fragment>
  );
};


export default GRVList;
